import { API_BASE_URL } from './forms';

export interface SubmitResponsePayload {
  projectId: string;
  formId: string;
  versionNumber: number;
  answers: Record<string, unknown>;
  startedAt: Date;
  skippedQuestionRefs: string[];
  questionPath: string[];
}

export interface ResponseSessionProgressPayload {
  answers: Record<string, unknown>;
  skippedQuestionRefs: string[];
  questionPath: string[];
  progressPercentage?: number;
  lastQuestionRef?: string;
  lastPageId?: string;
}

export interface CreateResponseSessionPayload extends SubmitResponsePayload {
  progressPercentage?: number;
  lastQuestionRef?: string;
  lastPageId?: string;
}

export interface LeaveResponseSessionPayload {
  answers?: Record<string, unknown>;
  skippedQuestionRefs?: string[];
  questionPath?: string[];
  progressPercentage?: number;
  lastQuestionRef?: string;
  lastPageId?: string;
}

export interface SubmitResponseSuccess {
  responseId: string;
  submittedAt: string;
}

export interface CreateResponseSessionSuccess {
  responseId: string;
  sessionToken: string;
  startedAt: string;
}

export interface SharedFormState {
  responseId: string;
  answers: Record<string, unknown>;
  updatedAt: string;
}

export interface ValidationError {
  questionRef: string;
  message: string;
  questionTitle?: string;
}

const getEndpoint = () => {
  const base = API_BASE_URL || '/api';
  return `${base.replace(/\/$/, '')}/responses`;
};

const getSharedStateEndpoint = (projectId: string, formId: string) => {
  const base = API_BASE_URL || '/api';
  return `${base.replace(/\/$/, '')}/forms/public/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}/shared-state`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getValidationErrors = (errorBody: ErrorBody | undefined): ValidationError[] => {
  const rawErrors = Array.isArray(errorBody?.error?.details?.errors)
    ? errorBody?.error?.details?.errors
    : [];

  return rawErrors
    .filter(
      (item): item is { questionRef: string; message: string; questionTitle?: string } =>
        Boolean(item) &&
        typeof item?.questionRef === 'string' &&
        typeof item?.message === 'string',
    )
    .map<ValidationError>((item) => ({
      questionRef: item.questionRef,
      message: item.message,
      questionTitle: item.questionTitle,
    }));
};

export class ResponseSubmissionError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number,
    public readonly questionErrors?: ValidationError[],
  ) {
    super(message);
    this.name = 'ResponseSubmissionError';
  }
}

type ErrorBody = {
  error?: {
    message?: string;
    code?: string;
    details?: {
      errors?: Array<{
        questionRef?: string;
        message?: string;
        questionTitle?: string;
      }>;
    };
  };
};

const toResponseSubmissionError = (status: number, payload: unknown): ResponseSubmissionError => {
  const errorBody: ErrorBody | undefined = isRecord(payload) ? (payload as ErrorBody) : undefined;
  const errorMessage =
    errorBody?.error?.message || 'Failed to submit form. Please try again.';
  const errorCode = errorBody?.error?.code || 'SERVER_ERROR';

  if (status === 400 && errorCode === 'VALIDATION_ERROR') {
    const questionErrors = getValidationErrors(errorBody);
    return new ResponseSubmissionError(
      errorMessage || 'Some answers are invalid.',
      'VALIDATION_ERROR',
      400,
      questionErrors,
    );
  }

  if (status === 404 || errorCode === 'FORM_NOT_FOUND') {
    return new ResponseSubmissionError(
      'This form is no longer available.',
      'FORM_NOT_FOUND',
      404,
    );
  }

  if (status === 429 || errorCode === 'RATE_LIMIT_EXCEEDED') {
    return new ResponseSubmissionError(
      'Too many submission attempts. Please wait a few minutes and try again.',
      'RATE_LIMIT_EXCEEDED',
      429,
    );
  }

  if (status === 401 && (errorCode === 'SESSION_TOKEN_INVALID' || errorCode === 'SESSION_TOKEN_EXPIRED')) {
    return new ResponseSubmissionError(errorMessage, errorCode, 401);
  }

  if (status >= 500 || errorCode === 'INTERNAL_SERVER_ERROR') {
    return new ResponseSubmissionError(
      errorMessage || 'Unexpected server error. Please try again later.',
      'SERVER_ERROR',
      status,
    );
  }

  return new ResponseSubmissionError(errorMessage, errorCode, status);
};

const requestJson = async <T>(endpoint: string, init: RequestInit): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(endpoint, init);
  } catch {
    throw new ResponseSubmissionError(
      'Network error. Please check your connection and try again.',
      'NETWORK_ERROR',
      0,
    );
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    data = undefined;
  }

  if (!response.ok) {
    throw toResponseSubmissionError(response.status, data);
  }

  if (!isRecord(data) || !('data' in data)) {
    throw new ResponseSubmissionError(
      'Unexpected response from server.',
      'SERVER_ERROR',
      response.status,
    );
  }

  const payload = (data as Record<string, unknown>).data;
  if (!isRecord(payload)) {
    throw new ResponseSubmissionError(
      'Unexpected response from server.',
      'SERVER_ERROR',
      response.status,
    );
  }

  return payload as T;
};

const toSessionPayload = (payload: ResponseSessionProgressPayload) => ({
  answers: payload.answers ?? {},
  skippedQuestionRefs: payload.skippedQuestionRefs ?? [],
  questionPath: payload.questionPath ?? [],
  progressPercentage: payload.progressPercentage,
  lastQuestionRef: payload.lastQuestionRef,
  lastPageId: payload.lastPageId,
});

export async function submitResponse(
  payload: SubmitResponsePayload,
): Promise<SubmitResponseSuccess> {
  const endpoint = getEndpoint();
  const result = await requestJson<SubmitResponseSuccess>(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...toSessionPayload(payload),
      projectId: payload.projectId,
      formId: payload.formId,
      versionNumber: payload.versionNumber,
      startedAt: payload.startedAt.toISOString(),
    }),
  });

  if (!result.responseId || !result.submittedAt) {
    throw new ResponseSubmissionError(
      'Incomplete response from server.',
      'SERVER_ERROR',
      500,
    );
  }

  return result;
}

export async function createResponseSession(
  payload: CreateResponseSessionPayload,
): Promise<CreateResponseSessionSuccess> {
  const endpoint = `${getEndpoint()}/sessions`;
  const result = await requestJson<CreateResponseSessionSuccess>(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...toSessionPayload(payload),
      projectId: payload.projectId,
      formId: payload.formId,
      versionNumber: payload.versionNumber,
      startedAt: payload.startedAt.toISOString(),
    }),
  });

  if (!result.responseId || !result.sessionToken || !result.startedAt) {
    throw new ResponseSubmissionError(
      'Incomplete response from server.',
      'SERVER_ERROR',
      500,
    );
  }

  return result;
}

export async function updateResponseSessionProgress(
  responseId: string,
  sessionToken: string,
  payload: ResponseSessionProgressPayload,
): Promise<void> {
  const endpoint = `${getEndpoint()}/sessions/${encodeURIComponent(responseId)}/progress`;
  await requestJson<{ response: unknown }>(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify(toSessionPayload(payload)),
  });
}

export async function getSharedFormState(
  projectId: string,
  formId: string,
): Promise<SharedFormState> {
  const result = await requestJson<{ state: SharedFormState }>(
    getSharedStateEndpoint(projectId, formId),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return result.state;
}

export async function updateSharedFormState(
  projectId: string,
  formId: string,
  answers: Record<string, unknown>,
  options?: { keepalive?: boolean },
): Promise<SharedFormState> {
  const result = await requestJson<{ state: SharedFormState }>(
    getSharedStateEndpoint(projectId, formId),
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: options?.keepalive,
      body: JSON.stringify({ answers }),
    },
  );

  return result.state;
}

export async function markResponseSessionLeave(
  responseId: string,
  sessionToken: string,
  payload: LeaveResponseSessionPayload = {},
): Promise<void> {
  const endpoint = `${getEndpoint()}/sessions/${encodeURIComponent(responseId)}/leave`;
  await requestJson<{ response: unknown }>(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify({
      ...payload,
      sessionToken,
    }),
    keepalive: true,
  });
}

export function sendResponseSessionLeaveBeacon(
  responseId: string,
  sessionToken: string,
  payload: LeaveResponseSessionPayload = {},
): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }

  const endpoint = `${getEndpoint()}/sessions/${encodeURIComponent(responseId)}/leave`;
  const blob = new Blob(
    [
      JSON.stringify({
        ...payload,
        sessionToken,
      }),
    ],
    { type: 'application/json' },
  );

  return navigator.sendBeacon(endpoint, blob);
}

export async function completeResponseSession(
  responseId: string,
  sessionToken: string,
  payload: ResponseSessionProgressPayload,
): Promise<SubmitResponseSuccess> {
  const endpoint = `${getEndpoint()}/sessions/${encodeURIComponent(responseId)}/complete`;
  const result = await requestJson<SubmitResponseSuccess>(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify(toSessionPayload(payload)),
  });

  if (!result.responseId || !result.submittedAt) {
    throw new ResponseSubmissionError(
      'Incomplete response from server.',
      'SERVER_ERROR',
      500,
    );
  }

  return result;
}
